/**
 * Main Component defintion that processes a page/step definition. 
 * This defines the form structure of the page, builds FormlyFieldConfig definitions
 * from the Mutara field definitions and provides default behaviors for field interactions
 * 
 * TBD: 
 * - convert the Mutara -> formly mapping to a separate module by extending FormlyFieldConfig
 * - create components for the desired behaviors (WIP)
 * 
 * @copyright Mutara, Inc. 2019-2026
 * @author Brian Sparling
 */

import { OnInit, Component, ViewEncapsulation, OnDestroy, ChangeDetectorRef, AfterViewInit, input } from '@angular/core';
import { FormDefinition, FormFieldDefinition, ServiceDataSet, StepDefinition } from '../dynamic';
import { AbstractControl, FormControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ContextService, RemoteDisplayValue, ServerServiceConfig, LoginData } from '../../context.service';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { BehaviorSubject, of } from 'rxjs';
import { ControlDataFinder, DynamicOptions, DateOptions, FieldFunction } from './control-util';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LayoutComponent } from '../../layout/layout.component';
import { afterNextRender } from '@angular/core';

@Component({
  selector: 'mutara-form',
  templateUrl: 'capture-form.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class CaptureFormComponent implements OnInit, AfterViewInit, OnDestroy {
  // All of these are passed to us
  readonly index = input<number | undefined>(undefined);
  stepDefinitions = input<StepDefinition[] | undefined>(undefined);
  readonly layout = input<LayoutComponent | undefined>(undefined);
  readonly submitText = input<string | undefined>(undefined); // this is the default text on submit
  // This is the working value of the Mutara form definition, set from the mutaraForm 
  formDefinition: FormDefinition = { formName: null, fields: [] };
  step: StepDefinition | undefined;

  isLast: boolean = false;
  // If vertical, do not display buttons in the scrolling form
  isVertical: boolean = false;
  // Allow to move to next step
  canMove: boolean = true;
  // This is the application configuration (lookups/version/theme/etc.)
  appConfig: ServerServiceConfig | undefined;
  // named validation logic for this formly form
  validators: any = {};
  // This is a record keyed object of field values that need to be accumulated
  deferredValues: any = {};
  // Here we track changes that can only be sent when a set of record fields non-null fields is complete (ex: address)
  _deferredChanges = new BehaviorSubject<any>(null);
  deferredChanges = this._deferredChanges.asObservable();
  // This is the map of the fields as they dynamically created
  fieldConfigs: FormlyFieldConfig[] = [];
  // Is this form embedded in a non-Mutara application frame
  isEmbed: boolean = false;
  // This is set outside our scope and we subscribe to the value
  login: LoginData | undefined; // Part of the contextual data
  // make accessing the data values easier
  ui: ControlDataFinder | undefined;
  /* Working local variables */
  // For each key field in the returned data, add to this map (should be returned as key.<record> in the original data)
  recordKeyMap: any = {};
  // Each record has a map of UI/database required fields
  recordRequiredFields: any = {};
  _footer: FormlyFieldConfig | undefined;
  // This is the flat map from group name to FormlyFieldConfig 
  groupFields: any = {};
  // map of field configurations in this form keyed by the flattened name "<group>.?<record>-<field>?.<subfield>"
  fieldConfigMap: any = {};
  tableGroups: FormlyFieldConfig[] = [];
  // text of the default submit button on the page. If empty, do not display
  buttonText: string | undefined;
  // Is this form enabled/editable
  enabled: boolean = true;

  // TODO: the form definition will be editable when the admin function is developed
  isEditable = false;
  // application name for the navigation 
  program: string | undefined;

  totalSteps: number = 0;
  nextStepLabel: string | undefined;

  fullyLoaded: boolean = false;
  subscriptions: Subscription[] = [];

  extraFunctions: Function[] = [];

  /**
  * Build up the internal handlers on the initialization of the component
  * 
  * @param locale - unused
  * @param actRouter - actived route handler
  * @param http - general http client for communicating with services
  * @param context - context services for inter-component communication
  */
  constructor(
    public http: HttpClient,
    public context: ContextService,
    public cdr: ChangeDetectorRef,
    private actRoute: ActivatedRoute) {
    /// BMS: Much better and timelier than processedSteps, but Formly is still slower
    // afterNextRender({
    //   write: () => {
    //     if (this.step) {
    //       this.step.isLoaded = true;
    //       if (this.stepDefinitions?.every(step => step.isLoaded)) {
    //         console.log('Sending Ready....');
    //         this.context.nextMessage("readyToReceive", true);
    //       }
    //     }
    //   }
    // });
  }

  get formModel(): any {
    return this.step?.dataModel;
  }

  get fieldDefinitions(): FormlyFieldConfig[] {
    return <FormlyFieldConfig[]>this.step?.fieldDefinitions;
  }
  get domForm(): UntypedFormGroup | UntypedFormArray {
    return <UntypedFormGroup | UntypedFormArray>this.step?.domForm;
  }
  get optionFormly(): FormlyFormOptions {
    return <FormlyFormOptions>this.step?.formlyOptions;
  }
  get debug(): boolean {
    return !environment.production;
  }
  /**
   * As the template is being processed, subscribe to changes (login,form, submission, etc.)
   */
  ngOnInit(): void {
    this.totalSteps = this.stepDefinitions()?.length ?? 0;
    this.isLast = this.index() == (this.stepDefinitions()?.length ?? 0) - 1;
    this.step = this.stepDefinitions()?.[this.index() ?? 0];
    if (this.step) {
      this.step.isLoaded = false;
      this.step.keyMaps = this.recordKeyMap;
      // This sets up the mapping for individual fields in this step
      this.step.fieldMaps = this.fieldConfigMap;
      this.step.validations = this.validators;
    } else {
      console.error('Invalid step index %O of %O', this.index(), this.stepDefinitions());
      return;
    }
    this.nextStepLabel = this.stepDefinitions()?.[(this.index() ?? -1) + 1]?.label ?? '';
    this.buttonText = this.submitText();

    // Each page gets its own copy of this so it is possible to be inconsistent
    this.ui = new ControlDataFinder(this);
    console.log('Init %O', this.stepName);
    if (this.stepName) {
      this.__loadForm();
    }

    // Get the structure and data to use in Formly (passed from the parent step)
    this.subscriptions.push(this.actRoute.queryParams.subscribe(params => {
      this.isEmbed = (params.embedded == 'true');
    }));
    // 
    // This enables the submit button
    this.subscriptions.push(this.context.subscribeTo("formComplete", (validity: { valid: boolean, label?: string }) => {
      this.enabled = validity.valid && this.isEditable;
      this.buttonText = validity?.label ?? this.submitText();
    }));
    // This triggers default button displays if horizontal
    this.subscriptions.push(this.context.subscribeTo("orientation", (isVertical: boolean) => {
      this.isVertical = isVertical;
    }));
    // Track the application number 
    this.subscriptions.push(this.context.subscribeTo("applicationNumber", (id: string) => {
      this.isEditable = ['NEW', 'ERROR'].includes(id ?? 'NEW');
    }));
    // System settings changers
    this.subscriptions.push(this.context.subscribeTo("applicationInfo", (appSettings: ServerServiceConfig) => {
      this.appConfig = appSettings;
    }));
    // Login data 
    this.subscriptions.push(this.context.subscribeTo("loginData", (login: LoginData) => {
      this.login = login;
      if (this.login?.username == '') {
        const stepValue = this.step;
        clearInterval(stepValue?.timer);
        if (stepValue) { stepValue.timer = null; }
      }
    }));
  }


  /**
   * The basic parts of the form are here, but we need to wait for the fields to show up
   */
  ngAfterViewInit() {
    // Fully loaded
    this.subscriptions.push(this.context.subscribeTo("readyToReceive", (allFormsLoaded: boolean) => {
      this.fullyLoaded = allFormsLoaded;
      // Trigger for saving of deferred changes 
      if (!!allFormsLoaded) {
        // console.log('Setup Subscriptions.... %O', this.stepName);
        Object.entries(this.fieldConfigMap).forEach(([key, formlyConfig]) => {
          let formly: FormlyFieldConfig = <FormlyFieldConfig>formlyConfig;
          if (!!formly.props?.lookupRemote) {
            formly.props?.sortableOptions?.setupSubscription();
          }
        });
        this.subscriptions.push(this._deferredChanges.subscribe(dataToSave => {
          if (!!dataToSave) {
            this._sendFieldChanges(dataToSave);
          }
        }));
        if (this.step?.startDataInterval) {
          this.step.startDataInterval();
        }
        this.cdr.markForCheck();
      }
    }));
  }
  /**
   * only set the is loaded when the fields are built
   */
  ngOnDestroy() {
    // console.log('Destroy %O', this.stepName);
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.ui?.destroy();
    delete this.ui;
    this.stepDefinitions.apply([]);
    this.step = undefined;
    Object.entries(this.fieldConfigMap).forEach(([, formlyConfig]) => {
      let formly: FormlyFieldConfig = <FormlyFieldConfig>formlyConfig;
      if (!!formly.props?.lookupRemote) {
        // console.log( '  free %O', formly.key);
        formly.props?.sortableOptions?.destroy();
        formly.props.sortableOptions = null;
      }
    });
    this.fieldConfigs = [];
    this.fieldConfigMap = {};
    this.tableGroups = [];
    this.fullyLoaded = false;
  }

  areObjectsEqual(obj1: any, obj2: any): boolean {
    if (obj1 === undefined || obj2 === undefined) {
      return false;
    }

    const type1 = typeof obj1, type2 = typeof obj2;
    if (type1 != type2) {
      return false;
    }
    if (type1 == 'string') {
      return obj1 == obj2;
    }

    if (Array.isArray(obj1) && Array.isArray(obj2)) {
      return obj1.length == obj2.length && obj1.every((value, index) => value === obj2[index]);
    }

    if ([obj1, obj2].includes(null)) {
      return obj1 === null && obj2 === null;
    }
    const entries1 = Object.entries(obj1);
    const entries2 = Object.entries(obj2);

    return entries1 == null && entries2 == null || (entries1.length == entries2.length && entries1.every(([key, value]) =>
      obj2.hasOwnProperty(key) && obj2[key] === value));
  }
  /**
   * Process the defaults passed by the service into useable values
   * 
   * @param defaultValues 
   */
  _applyDefaults(defaultValues: any, sourceData: any) {
    let defaults = {};
    if (!!defaultValues) {
      Object.keys(defaultValues).forEach(defaultKeyName => {
        let defaultKeyLayers: string[] = defaultKeyName.split('.'),
          currentLayer: any = defaults, currentSource: any = sourceData,
          valueKey: string | undefined = defaultKeyLayers.pop();
        // Walk this default value layers and sync the source data
        defaultKeyLayers.forEach(level => {
          if (!currentSource[level]) {
            currentSource = currentSource[level] = {};
          } else {
            currentSource = currentSource[level];
          }
          if (currentLayer) {
            if (!currentLayer[level]) {
              currentLayer = currentLayer[level] = {};
            } else {
              currentLayer = currentLayer[level];
            }
          }
        });
        if (Array.isArray(currentSource)) {
          currentSource.forEach((row: any) => {
            if (valueKey && row) {
              if (!row[valueKey]) {
                row[valueKey] = defaultValues[defaultKeyName];
              }
            }
          });
        } else if (!!currentSource && !!valueKey) {
          if (!currentSource[valueKey]) {
            currentSource[valueKey] = defaultValues[defaultKeyName];
          }
        }
      });
    }
    return sourceData;
  }
  /**
   * Call the service /form/ to load and process the form metadata and current data
   * for this login. 
   * 
   * This is the main layout management request. The layout steps are loaded 
   * asynchronously and processed 
   */
  __loadForm() {
    if (!!this.stepName) {
      let formParameters: any = { formName: this.stepName };
      if (!!this.formModel?._filter) {
        // console.log('Form has filters %O', this.formModel?._filter);
        Object.assign(formParameters, { _filter: JSON.stringify(this.formModel._filter) });
      }
      this.http.get<FormDefinition>('$service/form', {
        params: formParameters
      }).subscribe({
        error: (err) => {
          console.error(err);
        },
        next: (results: FormDefinition) => {
          this.formDefinition = results;
          try {
            // Create the formly form definition
            // Setup the data to be displayed 
            let serviceData = this._applyDefaults(results.defaults, { ...results.data });
            if (!!serviceData.$main) {
              serviceData = Object.assign(serviceData, serviceData.$main[0]);

              delete serviceData['$main'];
            }
            this.__processKeys(serviceData, true);
            const step = this.step;
            if (step) { step.dataModel = serviceData; }

            this._processFormDefinition();
            if (step) { step.fieldDefinitions = this.fieldConfigs; }
            // console.log('__loadForm %O steps %O', this.stepName, this.stepDefinitions());
            this.context.processedSteps(this.stepDefinitions() ?? [], step);

          } catch (e) {
            console.error(e);
          }
        }
      });
    }
  }


  /**
   * Called by the deferred field subscription 
   * @param dataToSet record data (with new field data) 
   * @returns  
   */
  _sendFieldChanges(dataToSend: any): void {
    // If there is data to send, send it and clear the deferred values 
    if (!dataToSend || !dataToSend.data.values || !this.isEditable || dataToSend.data.values?._isSending) {
      return;
    }
    dataToSend.data.values._isSending = true;
    dataToSend.data.type = !(dataToSend.data.keys = this.recordKeyMap[dataToSend.data.record]) ? 'insert' : 'update';
    if (this.debug) {
      console.groupCollapsed('SAVE DATA');
      console.log('%O changes %O(%O)', dataToSend.formName, dataToSend.data.record, dataToSend.data.type)
      console.dir(dataToSend.data.values, { depth: null });
      console.groupEnd();
    }
    // This updates the database to ensure that the data is maintained
    this.http.post<any[]>('$service/formdata', dataToSend).subscribe({
      error: (error) => {
        console.error('Sending Data Error %O %O', dataToSend, error);
        dataToSend.data.values._isSending = false;
      },
      next: (result: any[]) => {
        // this.cdr.markForCheck();
        dataToSend.data.values._isSending = false;
        if (!!result) {
          // This should be new keys to merge into the object
          try {
            result.forEach(row => {
              this.__findKeys(row);
              Object.keys(row).forEach(id => {
                if (!id.startsWith('key.')) {
                  let config: FormlyFieldConfig = this.fieldConfigMap[id];
                  if (!!config) {
                    config.formControl?.patchValue(config.type == 'datepicker' ? new Date(row[id]) : row[id]);
                  }
                }
              });
            });
          } catch {
            console.error('Error processing returned data %O', result);
          }
        } else {
          console.log('No results returned by %O', dataToSend);
        }
        this.cdr.markForCheck();
        // delete dataToSend.data.values._isSending;
      }
    });
  }

  // This is a helper to get the name of the step this is representing
  get stepName(): string | undefined {
    return this.step?.step;
  }

  /**
   * Find the value of the passed fieldConfig using the formly data model
   * @param fieldConfig 
   * @returns 
   */
  modelValue(fieldConfig: FormlyFieldConfig | undefined): any {
    if (!fieldConfig || !fieldConfig?.key) {
      return undefined;
    }
    let value: any;
    try {
      let keyParts = fieldConfig.key.toString().split('.');
      if (!!fieldConfig.model) {
        // let modelLocal = fieldConfig.model;
        // while (keyParts.length > 0) {
        //   try {
        //     modelLocal = modelLocal[keyParts?.shift()];
        //   } catch (err) {
        //     value = null;
        //   }
        // }
        value = keyParts.reduce((o, i) => (o === undefined ? undefined : o[i]), fieldConfig.model);
      } else {
        keyParts = fieldConfig.props?.decoratedId.split('.');
        value = this.formModel;
        keyParts.forEach(name => {
          try {
            value = value[name];
          } catch (e) {
            //console.error('modelValue value %O', e); 
            value = this.formModel[<string>fieldConfig.key];
          }
        });
      }
    } catch (e) {
      console.error('modelValue %O => %O', fieldConfig.key, e);
    }
    return value;
  }

  stopDataInterval() {
    if (!!this.step?.timer) {
      clearInterval(this.step.timer);
      this.step.timer = null;
    }
  }
  /**
   * Start the timer if there is an interval for this step
   */
  startDataInterval() {
    if ((this.step?.refreshInterval ?? -1) > 0) {
      // We have a timer running 
      if (!!this.step?.timer) {
        if (this.step.refreshCount >= 0 || this.step.refreshCount-- == 0) {
          this.stopDataInterval();
        }
      } else if (!!this.step) { // If no timer, and there is an interval, and the counter is still 0 or -1
        if ((this.step?.refreshInterval ?? -1) > 0 && (this.step?.refreshCount ?? -1) <= 0) {
          this.step.timer = setInterval(() => {
            this.http.get<FormDefinition>('$service/form', { params: { formName: this.step?.step ?? '' } }).subscribe({
              next: (results: FormDefinition) => {
                // Setup the data to be displayed
                this._processStepData(results?.data);
                // this passed the updated data back to the underlying table data subscription
                this.tableGroups.forEach((tabDef: FormlyFieldConfig) => {
                  let groupName: string = <string>tabDef.key;
                  if (!!tabDef.props?.dataSubject && !!results.data?.[groupName]) {
                    tabDef.props?.dataSubject.next(results.data[groupName]);
                  }
                });
              }
            });
          }, this.step.refreshInterval * 1000); // Refresh at an interval if specified
        }
      }
    }
  }

  /**
   * Expands the returned flat data into a folded object for use in Formly
   * 
   * @param flatObject flattened object to be folded
   * @param target target object 
   * @returns 
   */
  __expandObjectData(flatObject: any, target: any = {}, overwrite: boolean = true) {
    if (!!flatObject) {
      // Make this a multi-level structure. Each group may/may not impact placement of the children!
      Object.keys(flatObject).forEach(keyName => {
        // group.rec_name-field_name.sub_field == group, rec_name-field_name, sub_field
        let parts = keyName.split('.'), targetLevel = target, last = parts.length, value = flatObject[keyName];
        // Each level needs to be walked and collected
        parts.forEach((part, index) => {
          // Set the value at the bottom, or move down a level 
          if (last == index + 1) { // bottom
            if (overwrite || (!targetLevel[part] && !!value)) {
              targetLevel[part] = value;
            }
          } else if (targetLevel[part] === undefined) { // need to set the mid-tier
            targetLevel = targetLevel[part] = {};
          } else {
            targetLevel = targetLevel[part];
          }
        });
      });
      // This gives us a UI data value map keyed by group name
    }
    return target;
  }
  /** 
   * convert from the service data (flattened) to usable form data (hierarchical)
   */
  _processStepData(dataSet: any): any {
    let serviceData = {};
    if (!!dataSet) {
      // The main dataset needs to be converted to an indexable value (decorated->value) 
      if (!!dataSet['$main']) {
        serviceData = this.__expandObjectData(dataSet['$main'][0], serviceData);
        this.__findKeys(dataSet['$main']);
        delete dataSet['$main'];
      }

      // Now merge the data we received from the service 
      if (!!dataSet) {
        Object.assign(serviceData, dataSet);
      }
      // This needs to happen after processing the fields so that the data can be massaged (ex: columns, extra)
      // indexedData = Object.assign(this.formModel, serviceData);
    }

    this.__expandObjectData(this.formDefinition.defaults, serviceData, false); // Apply the defaults (if they apply)
    //console.log('%O -> data\n%O', this.step.step, this.formModel);
    return serviceData;
  }

  /** 
   * Helper to get the entire application data set in one structure 
   */
  get fullDataSet() {
    let ds = this.stepDefinitions()?.reduce((obj: any, step: StepDefinition) => {
      obj[step.step] = step.dataModel;
      return obj;
    }, {});
    return ds;
  }

  /** 
   * Build up the dataset to send to the service layer. This is based on required fields
   * in the record definitions. The deferred record is a set of values waiting to get 
   * sent on to the service layer which will store the values. 
   * 
   * This can only be an update (keys) or insert (no keys)
   */
  __buildDataSet(formlyField: FormlyFieldConfig, value: any = formlyField?.formControl?.value, forceSave: boolean = false): ServiceDataSet | undefined {
    // Safety checks
    if (!formlyField.formControl || !formlyField.props?.recordName || formlyField.formControl?.invalid ||
      (formlyField.formControl.value !== null
        && !formlyField.props?.isRequired
        && !!formlyField.props?.hidden)) {
      return undefined;
    }
    let keys = this.recordKeyMap[formlyField.props.recordName], currentValue = value;
    // Is this an encrypted mask? 
    if (!!formlyField.props?.encrypt && currentValue.length > 0) {
      if (currentValue.replace(/[#-]/g, '').length == 0) {
        return undefined;
      }
    }
    // This fixes issues with data that is too long
    if (!!formlyField.props.maxLength && formlyField.props.maxLength < currentValue.length) {
      formlyField.formControl.patchValue(currentValue = currentValue.substring(0, formlyField.props.maxLength));
    }

    // Find the deferred data for this record and add any special fields (ex: saveField)
    let deferred: any = this.__getDeferredRecord(formlyField.props.recordName), origData = { ...deferred }, isChanged: boolean = forceSave;
    // Here we can look for values to stash in the deferred values.... 
    if (!!formlyField.props.rdv) {
      if (formlyField.props.saveField != formlyField.key) {
        deferred = this.__addDeferredValue(this.fieldConfigMap[formlyField.props.saveField], formlyField.props.rdv?.currentValue, forceSave, deferred);
      }
    }
    // This should only update unchanged values (this field, not related deferred)
    if ((isChanged ||= (deferred[formlyField.props.decoratedId] != currentValue))) {
      deferred = this.__addDeferredValue(formlyField, currentValue, forceSave, deferred);
    }
    // if ( this.areObjectsEqual( origData, deferred ) ) {
    //   return undefined;
    // }
    // // Not changed || !hidden && forced
    if (!isChanged && !!formlyField.props.isRequired && (!formlyField.props.hidden || !forceSave)) {
      return undefined;
    }
    // Check if other related fields are still required (tricky because of delayed hide processing )
    let stillRequired = Object.keys((keys ?? {})).length > 0 ? [] : this.recordRequiredFields[formlyField.props.recordName]?.filter((dbField: string) => {
      let reqConfig = this.fieldConfigMap[dbField], isRequired = this.__requiredCheck(reqConfig);
      return !!deferred
        && (deferred[dbField] === undefined || deferred[dbField] === null)
        && (isRequired && (reqConfig.type != 'checkbox' || !!reqConfig.props.indeterminate));
    });

    // If there are values still required and no keys, we are done
    if (!!stillRequired && stillRequired.length > 0 && (!keys || keys.length == 0) || this.areObjectsEqual(origData, deferred) || !this.step) {
      return undefined;
    }
    // Now we can return what we know  
    return {
      formName: this.step?.step ?? '',
      data: {
        type: !keys ? 'insert' : 'update',
        record: formlyField.props.recordName,
        keys: keys,
        values: deferred
      }
    };
  }
  /**
   * Pass on the data to the service bound to context of user based on the field onChange event
   * @param formlyField
   */
  __triggerDataSave(formlyField: FormlyFieldConfig, $event: any = { value: formlyField?.formControl?.value }): void {
    try {
      if ($event.checked === undefined && $event.value === undefined) {
        $event.value = formlyField?.formControl?.value;
      }
      // This is prevents double saving of table data (once on field changes and again on Add/Update/Delete)
      if (!!formlyField?.props?.parentGroup?.wrappers.includes('table') || ($event.value === undefined && $event.checked === undefined)) {
        return undefined;
      }
      if (!!formlyField.props?.notify) {
        try {
          this.context.nextMessage(formlyField.props.notify, $event.value);
        } catch (e) {
          console.error('Field %O(%O) notification error %O', formlyField.key, $event.value, e);
        }
      }

      // Only pass along record-field values, others stay local
      let dataToSet: ServiceDataSet | undefined = this.__buildDataSet(formlyField, $event?.value ?? formlyField?.formControl?.value, $event?.forceSave);
      // Regardless of whether this is saved, keep it 
      if (!!dataToSet) {
        if (this.debug) {
          console.log('Save triggered by %O', formlyField.key);
        }
        this._deferredChanges.next(dataToSet);
      }
    } catch (err) {
      console.error('__triggerDataSave %O error: O%', formlyField.props?.decoratedId, err);
    }
  }

  /**
   * Check the field to see if the required field is visible
   * 
    * @param field 
   */
  __requiredCheck(field: FormlyFieldConfig): boolean {
    let isRequired = false;
    try {
      if (!field) {
        console.warn('%O', new Error('No field config passed to __requiredCheck'));
      }
      isRequired = !!field && !!field.props?.isRequired && !field.hide;

    } catch (err) {
      console.log('__requiredCheck error %O', err);
    }
    return isRequired;
  }

  /**
   * Check if the field state is disabled
   * @param field 
   * @returns 
   */
  __disableFieldValue(field: FormlyFieldConfig): boolean {
    let isEnabled: boolean = true;
    try {
      isEnabled = field?.props?.forceEnable ?? ((!field.props?.readonly && !!this.isEditable) || (field.props?.special == 'submitForm' && !field.props?.capForm?.enabled));
    } catch (err) {
      console.log('__disableFieldValue %O error: %O', field.props?.decoratedId, err);
    }
    return !isEnabled;
  }

  /**
   * Check the expression to see if we need to hide the field, default is no
   * 
   * @param field 
   */
  __hideCheck(model: any, formState: any, field: FormlyFieldConfig): boolean {
    let isHidden = !!field.props?.parentGroup?.hide || !!field.props?.forceHide || !!field.props?.hideSelf(formState, field);

    if (!!this.fullyLoaded && !!isHidden && field.props?.valueOnHide !== undefined && field.formControl?.value !== field.props.valueOnHide) {
      field.formControl?.setValue(field.props.valueOnHide);
      this.__triggerDataSave(field, { value: field.props.valueOnHide, forceSave: true });
      this.cdr.markForCheck();
    }
    if (field.props) {
      field.props.hidden = isHidden;
    }
    return isHidden;
  }

  /**
   * Helper to match the data to the target type. Allows a conversion to a value the database likes
   * @param formlyField 
   * @param value 
   * @returns 
   */
  __cleanUpData(formlyField: FormlyFieldConfig, value: any) {
    if (formlyField.type == 'datepicker' && !!value) {
      let date = new Date(value);

      value = date.getFullYear() + '-' + date.getMonth().toString().padStart(2, '0') + '-' + date.getDate().toString().padStart(2, '0');
    }
    return value;
  }
  /**
   * Helper to set a value into a group for an insert
   * @param formlyField 
   * @param value 
   * @param forceSave send the data to the service to ensure it is saved (hidden field changes)
   */
  __addDeferredValue(formlyField: FormlyFieldConfig, value: any, forceSave: boolean = false, deferredRecord: any = (!!formlyField ? this.__getDeferredRecord(formlyField.props?.recordName) : null)): any {
    let fieldId = formlyField?.props?.decoratedId;

    if (!!formlyField?.props?.recordName && value !== undefined) {

      if (!deferredRecord._isSending) {
        deferredRecord[fieldId] = value;// ?? this.formDefinition.defaults[fieldId]);
        if (!!formlyField && !!formlyField.props.rdv) {
          deferredRecord[formlyField.props.saveField] = formlyField.props.rdv?.currentValue;
        }
      } else {
        if (deferredRecord[fieldId] != value) {
          if (this.debug) {
            console.log('Deferring value set %O (%O)', formlyField.key, value);
          }
          setTimeout(() => this.__triggerDataSave(formlyField, { value: value, forceSave: forceSave }), 200);
        }
      }
    }
    return deferredRecord;
  }

  /**
   * Helper to identify the correct deferred record
   * @param fieldId 
   * @returns 
   */
  __getDeferredRecord(recordName: string) {
    let deferred;
    if (!!recordName) {
      deferred = this.deferredValues[recordName];
      if (!deferred) {
        deferred = this.deferredValues[recordName] = { $record: recordName };
      }
    }
    return deferred;
  }

  /**
   * Using the templateExtra definition, setup the field lifecycle hooks (component lifecycle)
   * This also sets up defers the setup of remote option lookups until the field is initialized
   * 
   * @param srvFieldDef 
   * @param fieldConfig 
   */
  _setupHooks(srvFieldDef: FormFieldDefinition, fieldConfig: FormlyFieldConfig): FormlyFieldConfig | undefined {
    // The data for hooks has a key that begins with an underscore '_'
    let hookList = Object.keys(srvFieldDef?.templateExtra ?? {}).filter(keyName => keyName.startsWith('_'));
    let hasTrigger = hookList.length > 0;

    // If the select or group has URI lookup, make sure the onInit will hook this to setup the reactive load of data
    if (fieldConfig.props?.isDynamicOption() || !!fieldConfig.props?.templateExtra?._onChanges) {
      if (!hookList.includes('_onChanges')) {
        hookList.push('_onChanges');
      }
    }

    // If there are hooks, drop them in
    fieldConfig.hooks = {
      // afterContentInit: (fld: FormlyFieldConfig) => {
      //   console.log('AfterContentInit %O %O %O', fld.key, fld.props?.trigger, fld.model);
      // }
    };
    if (hookList.length > 0) {
      hookList.forEach(hookKey => {
        switch (hookKey) {
          case '_onLoad': // Only called to initialize the lookups for RemotedDisplayValue
            // if (!fieldConfig.props?.lookupRemote) {
            if (fieldConfig.props && fieldConfig.hooks) {
              fieldConfig.props.postLoad = new FieldFunction(this, fieldConfig, hookKey);
              // Wait until the DOM view is setup to trigger the initialization
              fieldConfig.hooks.onInit = (fld: FormlyFieldConfig) => {
                console.log('OnInit %O', fld.key);
                this.subscriptions.push(this.context.subscribeTo("readyToReceive", (ready: boolean) => {
                  if (ready) {
                    let curVal = this.modelValue(fld);
                    if (this.debug) { console.log('_onLoad %O (afterViewInit) currentValue %O', fld.key, curVal); }
                    if (!!fld.props?.sortableOptions) {
                      fld.props.sortableOptions.selectedValue = curVal;
                    }
                    if (!!fld.props?.postLoad) {
                      fld.props.postLoad.callFunction(curVal);
                      fld?.formControl?.updateValueAndValidity();
                      delete fld.props.postLoad;
                    }
                  }
                }));
              };
            }
            break;
          case '_onChanges':
            if (fieldConfig.props && fieldConfig.hooks) {
              // This is called when the form field is created
              if (hasTrigger) { // create the trigger from the code in the definition
                fieldConfig.props.trigger = new FieldFunction(this, fieldConfig, hookKey);
              }
              // This should trigger values on initial state of this field
              fieldConfig.hooks.onChanges = (fld: FormlyFieldConfig) => {
                if (!!fld.props?.trigger) {
                  this.subscriptions.push(this.context.subscribeTo("readyToReceive", (ready: boolean) => {
                    if (ready && fld.props && !fld.props?.hooksSet) {
                      if (fld.formControl) {
                        this.subscriptions.push(fld.formControl.valueChanges.subscribe((newValue: any) => {
                          try {
                            if (this.debug) { console.log('_onChange %O(%O)', fld.props?.decoratedId, newValue); }
                            fld.props?.trigger.callFunction(newValue);

                          } catch (err) {
                            console.error('_onChanges %O error %O', fld.key, err);
                          }
                          fld.props?.capForm.cdr.markForCheck();
                        }));

                        fld.props.hooksSet = true;
                        if (this.debug) { console.log('UI Trigger %O initial set value %O', fld.props.decoratedId, this.modelValue(fld)); }
                        fld?.formControl?.updateValueAndValidity();
                      }
                    }
                  }));
                }
              };
            }
            break;
        }
      });
    }
    return (fieldConfig.props?.postLoad && !fieldConfig.props?.lookupField) ? fieldConfig : undefined;
  }

  /**
   * Create the options for a select field based on the lookupRemote and associated manipulators
   * (grouping, sorting, enable/disable) 
   * 
   * @param definition 
   * @param fieldConfig 
   */
  __addSelectDefinition(fieldConfig: FormlyFieldConfig) {
    // Map the specified lookups to the select based on the field name
    if (fieldConfig.props) {
      fieldConfig.props.typeaheadDebounceInterval = 300;
      fieldConfig.props.sortableOptions = new DynamicOptions(fieldConfig, this.ui);
    }
  }

  __createFieldConfig(srvFieldDef: FormFieldDefinition, remoteLookup: any | undefined): FormlyFieldConfig {
    return {
      key: srvFieldDef.fieldId,
      validators: {},
      // expressions overrides any of the properties of the field config. Here we add the major three
      expressions: {
        'props.required': (field: FormlyFieldConfig) => this.__requiredCheck(field),
        'props.disabled': (field: FormlyFieldConfig) => this.__disableFieldValue(field),
        hide: (field: FormlyFieldConfig) => this.__hideCheck(this.formModel, field.options?.formState, field)
      },
      modelOptions: { debounce: { default: 300 }, updateOn: "change" },
      resetOnHide: !(srvFieldDef.templateExtra?.noEmptyReset),
      //defaultValue: srvFieldDef.templateExtra?.initialValue ?? null,
      props: Object.assign({
        appearance: !!this.appConfig?.fill ? 'fill' : 'outline',
        capForm: this,
        ui: this.ui,
        dataModel: this.stepDefinitions(),
        stepName: this.stepName,
        precision: srvFieldDef.precision,
        label: srvFieldDef.label,
        description: srvFieldDef.hint,
        placeholder: srvFieldDef.placeholder,
        origDescr: srvFieldDef.hint,
        position: srvFieldDef.position,
        isRequired: !!srvFieldDef.required,
        readonly: !!srvFieldDef.readonly,
        recordName: srvFieldDef.record,
        lookupRemote: remoteLookup,
        loading: true,
        lookupField: srvFieldDef.lookupField,
        decoratedId: ((!!srvFieldDef.parentId ? srvFieldDef.parentId + '.' : '') + srvFieldDef.fieldId),
        // stepDefn: this.stepDefinitions.find(stepDefn => stepDefn.step == this.stepName),
        hideSelf: (formState: any, field: FormlyFieldConfig): boolean => {
          let isHidden: boolean = false;
          if (field.props?.hideCheckExpression) {
            try {
              isHidden ||= field.props.hideCheckExpression.call(field.props.capForm, field.model, formState, field);
            } catch (error) {
              console.error('Error executing hide expression: %O -> %O\n model: %O\nerror: %O', this.step?.step, field.props.decoratedId, field.model, error);
            }
          }
          return isHidden;
        },
        setValue: (value: any, forceSave: boolean = false) => {
          let fConfig = this.ui?.getFieldConfig(this.stepName, srvFieldDef.decoratedId);
          if (fConfig) {
            let mv = (fConfig.fieldGroup?.length ?? 0) == 0 ? this.modelValue(fConfig) : fConfig.model;
            if (!this.areObjectsEqual(mv, value)) {
              fConfig.formControl?.setValue(value);
            }
            if (!!forceSave) {
              this.__triggerDataSave(fConfig, { value: value, forceSave: forceSave });
            }
          }
        },
        getValue: () => { return this.ui ? this.modelValue(this.ui.getFieldConfig(this.stepName, srvFieldDef.decoratedId)) : undefined; },
        change: (field: FormlyFieldConfig, $event: any) => this.__triggerDataSave(field, $event),
        isDynamicOption: () => remoteLookup?.dynamic,
      }, srvFieldDef.templateExtra),
    }
  }
  /**
   * create the global validator for the field 
   * @param definition 
   */
  __createValidator(definition: FormFieldDefinition, fieldConfig: FormlyFieldConfig) {
    let validatorId = (!!definition.parentId ? definition.parentId + '.' : '') + definition.fieldId, validDefn;
    if (!!definition.validPattern) {
      try {
        // Try parsing, its ok if it doesn't parse
        this.validators[validatorId] = validDefn = JSON.parse(definition.validPattern);
      } catch (err: any) {
        let matches = err.message.match(/^.*position (?<position>\w+)$/);
        if (!!matches) {
          console.log('%O:\n  %O', err.message, definition.validPattern.substring(parseInt(matches.groups.position)));
        }
      }
      if (!!validDefn) {
        if (fieldConfig.validators) {
          fieldConfig.validators[validatorId] = {
            expression: (c: AbstractControl) => {
              let isValid = true;
              try {
                const defn = this.validators?.[validatorId];
                if (!!defn) {
                  isValid = (defn.type == null ? new RegExp(defn.pattern) : new RegExp(defn.pattern, defn.type)).test(c?.value);
                  if (defn.notIn && isValid) {
                    isValid = !defn.notIn.includes(c?.value);
                  }
                }
                if (!isValid) {
                  isValid ||= ([null, undefined, ''].includes(c?.value) && !fieldConfig.props?.isRequired);
                }
              } catch (eValid) {
                console.error('Problem with %O validator %O', definition.fieldId, eValid);
              }
              return isValid;

            },
            message: (error: string, field: FormlyFieldConfig) => {
              return field.formControl && !!field?.formControl.value ? `${field.props?.label} "${field.formControl.value}" ${definition.validMessage ?? 'is invalid'}` :
                `${field.props?.reviewLabel ?? field.props?.label} ${definition.validMessage ?? 'is required'}`;
            }
          }
        }
      }

      // Here we add a pattern for the init step when a pattern is pulled from a different field dynamically based on the value
      let dynamicPattern = definition?.templateExtra?.initialValidation;
      if (!!dynamicPattern) {
        validatorId = '_init-' + validatorId;
        if (this.appConfig?.lookups) {
          let lookupKey: any = this.modelValue(dynamicPattern.field),
            option: any = this.appConfig.lookups[dynamicPattern.option.source].find(option => option.value == lookupKey);
          dynamicPattern = !!option ? option[dynamicPattern.option.key] : validDefn.pattern;
        }
      } else {
        dynamicPattern = validDefn.pattern;
      }
      // Make sure this is a valid pattern to add
      if (!!dynamicPattern && typeof dynamicPattern === 'string') {
        try {
          this.validators[validatorId] = { pattern: dynamicPattern, type: validDefn.type };
        } catch (err) {
          console.error('%O Invalid validation pattern %O', definition.fieldId, dynamicPattern);
        }
      }
      /// If this is a file, add the isUploaded validation
      if (definition.fieldType.type == 'file') {
        if (fieldConfig.validators) {
          fieldConfig.validators.isUploaded = {
            expression: (c: AbstractControl) => !c.value || /\.*|\[.{1,}\]/.test(c.value),
            message: (error: any, field: FormlyFieldConfig) => `Please upload a file for "${field.props?.label}"`,
          };
        }
      }
    }

    // This sets up extra validations based on the defintion
    if (!!definition?.templateExtra?.validators) {
      definition?.templateExtra?.validators.forEach((validator: string) => {
        let validFunc, errorMessage;
        switch (validator) {
          case 'requiredTrue':
            validFunc = (c: AbstractControl) => !!c.value;
            errorMessage = (definition.templateExtra.reviewLabel) + ' must be checked';
            break;
          /*
          Structure 
          {
            <name>: { 
              condition: <logical expression with access to ui>
              message: <text of invalid message>
            }
          }
          */
          default: {
            let newValidation: any = validator;
            // the incoming is an object, we need the name as the validator
            let valDefn = newValidation[Object.keys(newValidation)[0]];
            // Now, create the function from text and set the message
            validFunc = new Function('c', 'ui', valDefn.condition);
            errorMessage = valDefn.message;
          }
            break;
        }
        if (fieldConfig.validators) {
          fieldConfig.validators[validator] = {
            expression: (control: AbstractControl) => validFunc(control, this.ui),
            message: (errorMessage: any, field: FormlyFieldConfig) => {
              const label: string = (field.props?.label ?? '');
              return `"${label.substring(0, 20)}${label.length > 20 ? '...' : ''}" must be checked`;
            },
          };
        }
      });
    }
    if (!!definition?.templateExtra?.validMessage) {
      definition.validMessage = definition?.templateExtra?.validMessage;
    }

  }
  __findDependantFields(expression: string): FormlyFieldConfig[] {
    let lstExpressions = expression.match(/getValue\('(?<section>\w+)','(?<decorated>\w+)'\)/g);
    console.log('Dependent Fields %O', lstExpressions);
    return [];
  }

  /**
   * This is the conversion from the simple service definition to the 
   * actionable form field definition with default value set. 
   * 
   * Part of the activity here is to manipulate the form data to make it follow the 
   * appropriate Formly data model prior to setting the data available
   * 
   * @param groups 
   * @param fieldDefn 
   */
  _buildField(srvFieldDef: FormFieldDefinition) {
    // Jump out if this is a bad field definition (NEVER)
    if ((srvFieldDef.fieldId ?? '') == '') {
      console.error('Field definition is missing keys %O', srvFieldDef);
      return;
    }

    if (!!srvFieldDef.templateExtra && typeof srvFieldDef.templateExtra === 'string') {
      console.error('%O malformed template extra (not JSON)\n  %O', srvFieldDef.fieldId, srvFieldDef.templateExtra);
      return;
    }
    // 
    let isDataField = srvFieldDef.fieldId.includes('-'), remoteLookup;
    // This defines the template select type options that are injected into the form
    try {
      if (!!srvFieldDef.lookupRemote) {
        remoteLookup = JSON.parse(srvFieldDef.lookupRemote);
      }
    } catch (err: any) {
      console.log('previous generation lookup found %O => %O', srvFieldDef.fieldId, srvFieldDef.lookupRemote);
      let parts: string[] = srvFieldDef.lookupRemote?.split('?') ?? [], parameters = {};
      if (parts.length > 1) {
        try {
          parameters = JSON.parse(parts[1]);
        } catch (err) {
          console.error('Bad lookup %O', srvFieldDef.lookupRemote);
        }
      }
      remoteLookup = parts.length > 1
        ? Object.assign(parameters, { dynamic: parts[0] })
        : { static: srvFieldDef.lookupRemote };
    }

    // Build up the Formly config from the database
    let fieldConfig: FormlyFieldConfig = this.__createFieldConfig(srvFieldDef, remoteLookup);

    let hideType = typeof srvFieldDef?.hideElement;
    if (hideType == 'string') {
      let expr = srvFieldDef.hideElement?.trim();
      if (!!expr) {
        if (!/^[.\s\S]*\{[.\s\S]*return[.\s\S]*\;[.\s\S]*\}[\s\S]*$/.test(expr)) { expr = 'return ' + expr + ';'; }
        if (fieldConfig.props && expr) {
          try {
            fieldConfig.props.hideCheckExpression = new Function('model', 'formState', 'field', expr);
          } catch (error) {
            console.error('buildField.hideCheckExpression error %O  - %O\n%O', fieldConfig.props.decoratedId, expr, error);
          }
        }
      }
    } else if (hideType == 'boolean') {
      if (fieldConfig.expressions) {
        fieldConfig.expressions.hide = () => <boolean><any>srvFieldDef.hideElement;
      }
    }

    // TODO: If there are expressions in the definition, add them
    let exprDefns = srvFieldDef.configExpressions?.properties;
    // if (!!exprDefns) {
    //   console.log('Expression defintion for %O', srvFieldDef.fieldId);
    //   delete srvFieldDef.configExpressions.expressions;
    // }

    // Now add the functions from configExpressions to the expression handler
    if (!!exprDefns) {
      Object.keys(exprDefns).forEach(propertyId => {
        try {
          let currentFunction = new Function(exprDefns[propertyId]);
          this.extraFunctions.push(currentFunction);
          if (fieldConfig.expressions) {
            (<any>fieldConfig.expressions[propertyId]) = (field: FormlyFieldConfig) => currentFunction.call(field);
          }
        } catch (error) {
          console.error('buildField.expression[%O] error %O  - %O\n%O', propertyId, fieldConfig.props?.decoratedId, exprDefns[propertyId], error);

        }
      });
    }

    // Make sure this is mapped to the key name for internal lookups
    this.fieldConfigMap[fieldConfig.props?.decoratedId] = fieldConfig;

    if (!!srvFieldDef.parentId) {
      if (!this.formModel[srvFieldDef.parentId]) {
        if (this.step?.dataModel) {
          this.step.dataModel[srvFieldDef.parentId] = {};
        }
      }
    }

    // Set the class for the field
    if (fieldConfig.props) {
      if (!srvFieldDef.fieldType.isGroup) {
        fieldConfig.type = srvFieldDef.fieldType.type;
        fieldConfig.className = fieldConfig.props._oldClass = fieldConfig?.props?.className ?? srvFieldDef.fieldType.className ?? 'flex-auto';
      } else {
        fieldConfig.className = fieldConfig.props._oldClass = fieldConfig?.props?.className;
        fieldConfig.fieldGroupClassName = srvFieldDef.fieldType.className;
      }
    }
    /*
     *  convert pattern to the expected Formly construct
     */
    this.__createValidator(srvFieldDef, fieldConfig);

    if (fieldConfig.type == 'encrypt' && fieldConfig.props) {
      fieldConfig.props.encrypt = true;
      fieldConfig.type = 'input';
    }

    // This converts the service definitions to the Formly template options for the UI
    switch (srvFieldDef.fieldType.type) {
      // Option group types
      case 'password':
        fieldConfig.type = 'input';
        fieldConfig.validators = {

          patterns: {
            expression: (c: AbstractControl) => /(?=.{8,})((?=.*\d)(?=.*[a-z])(?=.*[A-Z])|(?=.*\d)(?=.*[a-zA-Z])(?=.*[\W_])|(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_])).*/.test(c.value),
            message: () => 'Password rules: minmum 8 characters, at least 3 of the following; lowercase, uppercase, numbers, symbols'
          }
        }

        fieldConfig.props = Object.assign(fieldConfig.props ?? {},
          {
            type: 'password',
            hidePassword: true,
            placeholder: 'bubbl3Gum!',
            required: true,
            /*
            addonRight: {
              icon: 'visibility_off',
              ariaLabel: 'Show password',
              onClick: (props, field, $event) => {
                props.hidePassword = !props.hidePassword;
                props.type = props.hidePassword ? 'password' : 'text';
                props.addonRight.ariaLabel = props.hidePassword ? 'Show password' : 'Mask password';
                props.addonRight.icon = props.hidePassword ? 'visibility_off' : 'visibility';
              }
            }*/
          });
        break;
      case 'checkbox':
        fieldConfig.wrappers = [];
        if (fieldConfig.props && fieldConfig.props?.indeterminate === undefined) {
          fieldConfig.props.indeterminate = this.appConfig?.indeterminateCheckbox ?? false;
        }
        let currentValue = this.modelValue(fieldConfig);
        // convert to boolean in the model, or in the default
        if (typeof currentValue === 'string' && this.ui) {
          this.ui.setValue(this.stepName, fieldConfig.props?.decoratedId, ['true', '1'].includes(currentValue.toLowerCase()));
        }
        break;
      // This is the set of types that produce lists or lookups
      case 'radio-group':
      case 'typeahead':
      case 'select':
      case 'radio':
        if (!fieldConfig.className) {
          fieldConfig.className = 'flex-full';
        }
        // parse and store the value correctly
        if (!!srvFieldDef.templateExtra?.multiple && this.formModel[srvFieldDef.fieldId] !== undefined) {
          try {
            let jsonValue = JSON.parse(this.formModel[srvFieldDef.fieldId]) ?? [];
            let parts = srvFieldDef?.decoratedId?.split('.') ?? [], value = this.formModel, last = parts.pop();
            parts.forEach(part => value = value[part]);
            if (value && last !== undefined) {
              value[last] = jsonValue;
            }
          } catch (e) {
            console.error('multiple select data conversion error %O', e);
          }
        }
      case 'file':
        this.__addSelectDefinition(fieldConfig);
        if ((fieldConfig?.props?.extraDataFields?.length ?? 0) > 0) {
          fieldConfig.wrappers = ['extra-fields'];
        }
        break;
      // Simple types
      case 'column-break':
        fieldConfig.type = undefined;
        fieldConfig.template = `<section>${srvFieldDef.label ?? '&nbsp;'}</section>`;
        break;
      case 'break':
        fieldConfig.type = undefined;
        fieldConfig.template = `<section class='mutara-tb-margin'><span class='mutara-strong'>${srvFieldDef.label ?? ''}</span> ${!!srvFieldDef.hint ? "<span class='mutara-subtitle'>" + srvFieldDef.hint + "</span>" : ''} </section>`;
        break;
      case 'datepicker':
        if (fieldConfig.props) {
          DateOptions.options(fieldConfig);
          fieldConfig.props.blur = fieldConfig.props.change;
          /* BUGS 10382, 10782, 10783 */
          if (fieldConfig.modelOptions) {
            fieldConfig.modelOptions.updateOn = 'blur';
          }
        }
        /* 10382, 10782, 10783 */
        let srvDt = this.formModel[<string>fieldConfig.key];
        if (!!srvDt && typeof srvDt === 'string') {
          //console.log('Creating %O date %O', fieldConfig.key, srvDt);
          let dt = new Date(srvDt),
            gFld: FormlyFieldConfig = !srvFieldDef.parentId ? undefined : this.groupFields[srvFieldDef.parentId];
          if (!!gFld && !!gFld.props?.layout) {
            if (gFld.props.precision == -1) {
              this.formModel[gFld.props.layout][<string>gFld.key][<string>fieldConfig.key] = dt;
            }
          } else {
            this.formModel[<string>gFld.key][<string>fieldConfig.key] = dt;
          }
        }
        break;
      case 'color-picker':
      case 'review':
        break;
      case 'phone':
        if (fieldConfig.props) {
          fieldConfig.props.hrefType = 'link';
          fieldConfig.props.hrefPrefix = 'tel:';
          fieldConfig.props._asyncControl = new FieldFunction(this, fieldConfig, '_asyncValidation');
          fieldConfig.props.blur = fieldConfig.props?.change;
          /* BUGS 10382, 10782, 10783 */
          if (fieldConfig.modelOptions) {
            fieldConfig.modelOptions.updateOn = 'blur';
          }
        }
        fieldConfig.asyncValidators = {
          checkPhone: {
            expression: (c: UntypedFormControl) => {
              if ((c.value?.length ?? 0) < 8) return of(false); // minimum of 8
              let prefix = 'USA';
              if (!!fieldConfig.props?.prefixName) {
                let prefixField = this.ui?.getFieldConfig(this.stepName, fieldConfig.props.prefixName);
                prefix = prefixField?.formControl?.value;
              }
              const phoneNumber = c.value;
              return new Promise((resolve) => {
                if (!!phoneNumber) {
                  this.http.post('$service/sms-lookup', { phone: phoneNumber, prefix: prefix }).subscribe((result: any) => {
                    // No phone number return means it is not valid
                    if (fieldConfig.props) {
                      if (!result?.phoneNumber) {
                        //  fieldConfig.className = 'invalid';
                        fieldConfig.props.description = 'Please provide a valid phone number'
                        resolve(false);
                      } else {
                        fieldConfig.props.description = fieldConfig.props.origDescr;
                        resolve(true);
                      }
                    }
                  });
                  // this.context.updateValidity();
                }
              });
            },
            message: (error: string, field: FormlyFieldConfig) => `${error} ${field.formControl?.value}`
          }
        }
        break;
      // Action field types
      case 'button':
        if (fieldConfig.props) { fieldConfig.props.hrefType = 'button'; }
        break;
      case 'website':
        if (fieldConfig.props) { fieldConfig.props.hrefPrefix = 'https:' }
      case 'email':
        fieldConfig.type = 'input'; // These are specialized input 
        if (fieldConfig.props) {
          fieldConfig.props.hrefType = 'link';
          fieldConfig.props.hrefPrefix = fieldConfig.props.hrefPrefix ?? 'mailto:';
        }
        fieldConfig.validators = {
          email:
          {
            expression: (c: AbstractControl) => /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i.test(c.value),
            message: () => !fieldConfig.props?.validEmail.value ? 'Email address does not exist' : 'Invalid email address format'
          }
        };
        fieldConfig.asyncValidators = {
          emailTest: {
            expression: (c: AbstractControl) => {
              let emailProm = new Promise((resolve) => {
                this.http.post<any>('$service/check-email', { email: c.value }).subscribe({
                  error: (err) => {
                    console.log('Check Email Error %O', err);
                  },
                  next: (result: any) => {
                    console.log('Check-Mail returns %O', result);
                    // The returned verdict (Valid, Risky, Invalid, Duplicate, Error)
                    if (!['Valid', 'Risky'].includes(result?.verdict)) {
                      // If there is a suggestion, we reset the value to it
                      if (!!result?.suggestion) {
                        c.setValue(result.local + '@' + result.suggestion);
                      }
                      if ( fieldConfig.props ) {
                        fieldConfig.props.emailError = result.message;
                      }
                      
                      resolve(false);
                    }
                    // If there is a suggestion, we reset the value to it
                    if (!!result?.suggestion) {
                      c.setValue(result.local + '@' + result.suggestion);
                    }
                    resolve(true);
                  }
                });
              });
              return emailProm;
            },
            message: (error: any, field: FormlyFieldConfig) =>  `${field?.props?.emailError ?? error.message.toString()} This email address is not valid`
          }
        }
        fieldConfig.hooks = {
          afterViewInit: (fld: FormlyFieldConfig) => {
            if (fld.props && fld.props.blur) {
              fld.props.blur(fld);
            }
          }
        }
        break;
      // Here are our controllers (wrappers) 
      case 'address': // Now a google place lookup
      case 'button-row':
      case 'agreement':
      case 'file-checklist':
      case 'combine-values':
        fieldConfig.wrappers = [srvFieldDef.fieldType.type];
        if (!srvFieldDef.fieldType.isGroup) {
          break;
        }
        fieldConfig.type = undefined;
      // This is the class of fields grouped into a form group
      case 'group':
      case 'columns':
      case 'footer':
      //case 'column-break':
      case 'payment-form':
        // create the array within the group 
        let thisGroup = this.groupFields[srvFieldDef.fieldId];
        if (!thisGroup) {
          thisGroup = this.groupFields[srvFieldDef.fieldId] = fieldConfig;
          if (fieldConfig.props) {
            fieldConfig.props.formName = this.step?.step;
            fieldConfig.props.precision = srvFieldDef?.precision;
          }
          fieldConfig.fieldGroup = [];

          if (!fieldConfig.validators) {
            fieldConfig.validators = {};
          }
          fieldConfig.validators[srvFieldDef.fieldId] = {
            expression: (c: AbstractControl | any) => (c?.precision ?? 0) - (c?.size ?? 0)
          }
          // Need to add a table to handle multiple fields
          this.tableGroups.push(thisGroup);

          // This needs to be an observable!! 
          // if (!this.modelValue(fieldConfig)) {
          //   this.ui.setValue(this.stepName, fieldConfig.props.decoratedId, []);
          // }
        }
        if (!fieldConfig.wrappers) {
          // assign the field configuration and add the wrapper for the group
          fieldConfig.wrappers = [(srvFieldDef?.precision ?? 0) == 0 ? srvFieldDef.fieldType.type : 'table'];
        }
        if (!!thisGroup) {
          Object.assign(thisGroup, fieldConfig);
        }
        break;
    }

    // Set the value displayed -- this can be either default or actual data
    let value = this.modelValue(fieldConfig);
    if (fieldConfig.type == 'datepicker' && !!value) {
      this.ui?.setValue(this.stepName, fieldConfig.props?.decoratedId, value = new Date(value));
    }

    /* Typeahead has a second field (saveField) that needs to be updated in sync */
    if (srvFieldDef.fieldType.type == 'typeahead' && !!srvFieldDef?.templateExtra?.saveField) {
      if (fieldConfig.props) { fieldConfig.props.rdv = new RemoteDisplayValue(); }
      // Wait until the UI finishes to setup the observables
      this.subscriptions.push(this.context.subscribeTo("readyToReceive", {
        next: (allStepsReady: boolean) => {
          if (!!allStepsReady) {
            let displayValue: RemoteDisplayValue = fieldConfig.props?.rdv, saveField = fieldConfig.props?.saveField;
            if (displayValue && !displayValue.fieldId) {
              // This is the name of the returning data from the lookup (extra field added to the value set) 
              displayValue.fieldId = srvFieldDef.lookupField;
              displayValue.step = this.stepName;
              // Initialize the current value or display from the loaded data
              if (!!saveField && !!this.ui?.getValue(this.stepName, saveField)) { // this gives us the last label typed/found
                displayValue.setDisplay(this.ui?.getValue(this.stepName, saveField) ?? '');
              } else if (!srvFieldDef.parentId || !this.tableGroups.find(group => group.key == srvFieldDef.parentId)) {
                displayValue.setDisplayFromLocal(this.ui);
              }
            }
          }
        }
      }));
    }

    // Map the not null fields required to their records so we can correctly defer data until all required data is present
    if (isDataField) {
      if (!srvFieldDef.nullable || !!srvFieldDef.required) {
        // Build the record map from the fieldId value
        let dbField = srvFieldDef.fieldId.split('-');
        // If there is a record name and no key, create this object
        let requiredList = this.recordRequiredFields[dbField[0]];
        if (!requiredList) {
          requiredList = this.recordRequiredFields[dbField[0]] = [];
        }
        requiredList.push(fieldConfig.props?.decoratedId);
      }
      // Copy the current value from the model to the deferred record mapping
      this.__addDeferredValue(fieldConfig, (fieldConfig.props?.encrypt ? '' : this.modelValue(fieldConfig)), true);
    }

    // Now we can match the grouping depth for the data value to the structure and/or set the default values 
    if (!srvFieldDef.parentId) {
      // check for group in group
      if (!!fieldConfig.props?.layout) {
        let layoutName = fieldConfig.props?.layout, wrappingLayout = this.tableGroups.find(group => group.key == layoutName);
        if (!!layoutName && !!wrappingLayout) {
          fieldConfig.props.parentId = layoutName;
          wrappingLayout.fieldGroup?.push(fieldConfig);
          // Here we need to adjust the model placement
          let layoutNode = this.formModel[layoutName];
          if (!layoutNode && this.step?.dataModel) {
            this.step.dataModel[layoutName] = layoutNode = {};
          }
          layoutNode[<string>fieldConfig.key] = { ...this.formModel[<string>fieldConfig.key] };
          if (this.step?.dataModel) { delete this.step.dataModel[<string>fieldConfig.key]; }
        } else {
          this.fieldConfigs.push(fieldConfig);
        }
      } else {
        this.fieldConfigs.push(fieldConfig);
      }
    } else { // find the group
      if (!!this.groupFields[srvFieldDef.parentId]) {
        let group = this.groupFields[srvFieldDef.parentId];
        if (this.tableGroups.includes(group) && fieldConfig.props) {
          fieldConfig.props.parentGroup = group;
        }
        group.fieldGroup.push(fieldConfig);
      } else {
        console.debug('Group %O|%O not available for field %O. Probable misorder of group to field', this.stepName, srvFieldDef.parentId, srvFieldDef.fieldId);
      }
    }
    if (!!fieldConfig.props?.optionFilter) {
      fieldConfig.props.optionFilter = new FieldFunction(this, fieldConfig, 'optionFilter');
    }
    // Need to trigger a save 
    //fieldConfig.props._submitValue = ((value === undefined || value === null));
    // Setup the hooks to be called after the SPA is initialized and the data is present
    return this._setupHooks(srvFieldDef, fieldConfig);
  }

  /**
   * add a field/value to the key map for this record
   * 
   * @param recordName Record
   * @param fieldName 
   * @param value 
   */
  __addKey(recordName: string, fieldName: string, value: any) {
    if (!!value) {
      let recordKeys = this.recordKeyMap[recordName];
      if (!recordKeys) {
        recordKeys = this.recordKeyMap[recordName] = {};
      }
      recordKeys[fieldName] = value;
    }
  }
  /**
   * create the subdata sets from the data result set(s)
   */
  __findKeys(dataSet: any): void {
    if (Array.isArray(dataSet)) {
      dataSet.forEach(sqlData => this.__processKeys(sqlData));
    } else {
      this.__processKeys(dataSet);
    }
  }

  /**
   * Find key values for all the records
   * @param dataSet 
   * @param removeFromData 
   */
  __processKeys(dataSet: any, removeFromData: boolean = false): void {
    let valuesToDelete: string[] = [];
    Object.keys(dataSet).filter(keyName => keyName.startsWith('key.')).forEach(key => {
      let value = dataSet[key]; // Finds the value in this set         
      if (removeFromData) {
        valuesToDelete.push(key);
      }
      if (!!value) { // no undefined or null
        key = key.slice(4);
        let keyDefn = key.split('-'); //  0 = record 1 = field
        this.__addKey(keyDefn[0], key, value);
      }
    });
    //  valuesToDelete.forEach(keyName => delete dataSet[keyName])
  }

  /**
   * Invoked on change of formDefinition, this processes the form definition (step) and 
   * includes data that should be displayed/edited in this specific section of the form
   */
  _processFormDefinition() {
    const formDefinition = this.formDefinition;
    if (!formDefinition) {
      return;
    }

    let onLoadTrigger: any[] = [];
    // Walk the returned field defintions and map to FormlyFieldConfig structure
    formDefinition?.fields?.forEach(element => {
      try {
        let postInitialize = this._buildField(element);
        if (!!postInitialize) {
          onLoadTrigger.push(postInitialize);
        }
      } catch (e) {
        console.error('definition error in field %O %O', element, e);
      }
    });

    // This happens once at the initialization of the form, and it includes a self reference in cdf for 
    if (this.step && this.step.formlyOptions) {
      this.step.formlyOptions.formState.groups = !!formDefinition.fields ? formDefinition.fields?.filter((def: any) => def.fieldType.isGroup)
        ?.reduce((acc: any, def) => {
          acc[def.fieldId] = def;
          return acc;
        }, {}) : [];
    }
    this._modifyTableForms();

    // Error check of empty form
    if (!formDefinition.fields) {
      throw Error('Invalid form definition ' + this.step?.step);
    }
    // this.step.formlyOptions.detectChanges = this.__triggerDataSave;
    // ********
    // BMS: TODO: formState is avaliable everywhere. Make this a set of GP utilities
    // ********
    if (this.step?.formlyOptions) {
      Object.assign(this.step.formlyOptions.formState ?? {}, {
        data: this.fullDataSet, // All the data available in UI format
        ui: this.ui,
        capForm: this,
        embedded: this.isEmbed
      });
    }
    // ********


    // This is where we are notified that all of the sections are loaded
    this.subscriptions.push(this.context.subscribeTo("readyToReceive", (isLoaded: boolean) => {
      if (isLoaded) {
        onLoadTrigger.forEach((obj) => {
          if (!!obj.props.postLoad && !!obj.defaultValue) {
            obj.props?.postLoad.callFunction(obj.defaultValue);
          }
        });
      }
    }));
  }

  /**
   * Table groups need the extra buttons to manage the underlying data rows
   */
  _modifyTableForms() {
    try {
      this.tableGroups?.forEach((group) => {
        // Here we need to set the required as a call to the underlying field
        if (group.wrappers && group.wrappers.includes('table') && group.fieldGroup) {
          group.fieldGroup.push({
            key: group.key + '_add',
            type: 'addrow',
            props: {
              buttons: group.props?.readonly || !this.isEditable ?
                {
                  update: { label: 'Resubmit', icon: 'redo', action: '$service/queue-restart', visible: true },
                } : {
                  create: { label: 'Add', icon: 'add', visible: true },
                  update: { label: 'Update', icon: 'done', visible: true },
                  edit: { label: 'Edit', icon: 'edit', visible: true },
                  cancel: { label: 'Cancel', icon: 'cancel', visible: true },
                  delete: { label: 'Delete', icon: 'delete', visible: true }
                },
            }
          });
        }
      });
    } catch (e) {
      console.error('groups definition error %O', e);
    }
    // Error check of empty form
    if (!this.formDefinition?.fields) {
      throw Error('Invalid form definition ' + this.step?.step);
    }
  }
}
